// src/pages/DashboardPage.js

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Correctly imported hooks
import Joyride from 'react-joyride';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Your components
import MainNavbar from '../../components/MainNavbar';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import RankCard from '../../components/dashboard/RankCard';
import ActionCard from '../../components/dashboard/ActionCard';
import Toast from '../../components/common/Toast';
import DashboardContentTabs from '../../components/dashboard/DashboardContentTabs';

// Your Skeletons
import DashboardHeaderSkeleton from '../../components/dashboard/skeletons/DashboardHeaderSkeleton';
import ActionCardSkeleton from '../../components/dashboard/skeletons/ActionCardSkeleton';
import DashboardContentTabsSkeleton from '../../components/dashboard/skeletons/DashboardContentTabsSkeleton';

import { useAuth } from '../../firebase/useAuth';
import { markTourAsFinished } from '../../firebase/user';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { useForecast } from '../../contexts/ForecastProvider';
import Alert from '../../components/common/Alert';
import { computeStreak, getVacationDates } from '../../utils/streak';
import moment from 'moment-timezone';

function DashboardPage() {
  const { userId, userData, userLoading, emailVerified } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const errorMessage = location.state?.errorMessage;

  // Global data hooks
  const { learnedTodayData, reviewedSentencesData, reviewedWordsData, playedSentencesData, playedWordsData, learnedWordIds, learnedSentenceIds, listening, loading: metadataLoading } = useMetadata();
  const { stats, loading: dashboardLoading } = useDashboardStats();
  const { alerts, alertsLoading, cramWordsCount, cramSentencesCount, cramCountsLoading, recentUnlocksWordsCount, recentUnlocksSentencesCount, recentUnlocksCountsLoading } = useForecast();
  // State
  const [runTour, setRunTour] = useState(false);
  const [toastInfo, setToastInfo] = useState({ show: false, message: '', type: 'info' });

  const isLoading = userLoading || dashboardLoading || metadataLoading || !stats || alertsLoading || cramCountsLoading || recentUnlocksCountsLoading;

  const userTimezone = useMemo(() => userData?.settings?.timezone || 'UTC', [userData?.settings?.timezone]);
  const onVacation = useMemo(() => !!userData?.settings?.vacation_started_at, [userData?.settings?.vacation_started_at]);
  // Not memoized — must recompute on every render so todayDate stays current when the
  // date changes at midnight (learnedTodayData itself won't change until user learns something).
  const learnedToday = learnedTodayData?.[moment().tz(userTimezone).format('YYYY-MM-DD')] || 0;
  const isUserHourlySettingEnabled = useMemo(() => userData?.settings?.review_frequency === 'hour', [userData?.settings?.review_frequency]);

  const showToast = (message, type = 'info') => {
    setToastInfo({ show: true, message, type });
  };

  const processForecast = useCallback(
    (forecast) => {
      const dailyForecast = forecast || {};
      let cumulativeReviews = 0;
      const next7Days = Array.from({ length: 7 }, (_, i) => moment().tz(userTimezone).add(i, 'days').format('YYYY-MM-DD'));
      
      return next7Days.map((dayKey) => {
        const dailyCount = dailyForecast[dayKey] || 0;
        cumulativeReviews += dailyCount;
        const dayMoment = moment(dayKey);

        return {
          day: dayMoment.format('ddd D'),
          fullDate: dayKey, // Add full date for tooltip calculation
          reviews: onVacation ? 0 : cumulativeReviews,
          hourData: dailyForecast[`${dayKey}:hourly`] || null,
        };
      });
    },
    [userTimezone, onVacation]
  );

  // Tour and error handling effect
  useEffect(() => {
    if (userData && !isLoading) {
      if (errorMessage) {
        showToast(errorMessage, 'error');
        navigate(location.pathname, { replace: true });
      }

      if (!userData.finished_tour) {
        setRunTour(true);
      }
    }
  }, [userData, isLoading, errorMessage, navigate, location.pathname]);

  const { streak: currentStreak, shields, protectedDaysUsed, protectedDates } = useMemo(
    () =>
      computeStreak({
        learnedTodayData,
        reviewedWordsData,
        reviewedSentencesData,
        userTimezone,
        vacationStartedAt: userData?.settings?.vacation_started_at,
        vacationPeriods: userData?.vacation_history,
      }),
    [
      learnedTodayData,
      reviewedWordsData,
      reviewedSentencesData,
      userTimezone,
      userData?.settings?.vacation_started_at,
      userData?.vacation_history,
    ]
  );

  // Vacation days shown on the heatmap, across the user's full activity history
  // (the heatmap is scrollable through all months). Computed independently of the
  // streak walk so they're marked even in months where the streak later broke.
  const vacationDates = useMemo(() => {
    const now = moment().tz(userTimezone);
    const keys = [
      ...Object.keys(learnedTodayData || {}),
      ...Object.keys(reviewedWordsData || {}),
      ...Object.keys(reviewedSentencesData || {}),
    ];
    let startKey = now.clone().startOf('month').format('YYYY-MM-DD');
    if (keys.length) {
      const earliest = keys.reduce((min, k) => (k < min ? k : min), keys[0]);
      if (earliest < startKey) startKey = earliest;
    }
    return getVacationDates({
      vacationStartedAt: userData?.settings?.vacation_started_at,
      vacationPeriods: userData?.vacation_history,
      userTimezone,
      start: startKey,
      end: now.format('YYYY-MM-DD'),
    });
  }, [
    userTimezone,
    userData?.settings?.vacation_started_at,
    userData?.vacation_history,
    learnedTodayData,
    reviewedWordsData,
    reviewedSentencesData,
  ]);

  const upcomingReviewsSentences = useMemo(
    () => processForecast(stats?.sentencesStats?.reviewForecast),
    [processForecast, stats?.sentencesStats?.reviewForecast]
  );
  const upcomingReviewsWords = useMemo(
    () => processForecast(stats?.wordsStats?.reviewForecast),
    [processForecast, stats?.wordsStats?.reviewForecast]
  );

  const handleTourCallback = (data) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      markTourAsFinished(userId);
      setRunTour(false);
    }
  };

  const tourSteps = [
    {
      target: '.learning-button',
      content: emailVerified
        ? 'Start learning new words and sentences here!'
        : 'Start learning new words and sentences here! Please make sure to verify your email.',
      disableBeacon: true,
    },
    {
      target: '.review-sentences-button',
      content: "Review items you've already learned to commit them to memory.",
    },
    {
      target: '.review-words-button',
      content: "Review individual words you've added from sentences.",
    },
    {
      target: '.tab-navigation',
      content: 'Use these tabs to explore different aspects of your learning progress.',
    },
    {
      target: '.milestones-section',
      content: 'Track your long-term achievements and milestones here!',
    },
    {
      target: 'body',
      content: "That's it! Happy listening! 🦊🎧",
      placement: 'center',
    },
  ];

  const vacationStartedAt = userData?.settings?.vacation_started_at;

  const totalLearnedItems = useMemo(() => {
    if (!stats) return 0;
    return (stats.sentencesStats?.totalLearned || 0) + (stats.wordsStats?.totalLearned || 0);
  }, [stats]);

  const totalReviewedItems = useMemo(() => {
    if (!reviewedSentencesData || !reviewedWordsData) return 0;
    const reviewedSentencesCount = Object.values(reviewedSentencesData).reduce((sum, count) => sum + count, 0);
    const reviewedWordsCount = Object.values(reviewedWordsData).reduce((sum, count) => sum + count, 0);
    return reviewedSentencesCount + reviewedWordsCount;
  }, [reviewedSentencesData, reviewedWordsData]);

  const listeningSeconds = listening.totalSeconds;

  const { sentencesStats, wordsStats } = stats || {};

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <Toast message={toastInfo.message} type={toastInfo.type} show={toastInfo.show} onClose={() => setToastInfo({ ...toastInfo, show: false })} />
      {!isLoading && (
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous
          showSkipButton
          scrollToFirstStep={false}
          scrollOffset={220}
          locale={{
            back: 'Go Back',
            last: 'Start Listening!',
            next: 'Next',
            skip: 'Skip Tour',
          }}
          callback={handleTourCallback}
          styles={{
            options: { primaryColor: 'var(--color-primary)', zIndex: 1000 },
            tooltip: {
              maxWidth: 'calc(100vw - 32px)',
              width: '100%',
            },
          }}
        />
      )}
      <div className="flex min-h-screen flex-col bg-background-primary">
        <MainNavbar />
        <main className="flex-grow p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl space-y-6">
            {/*-- 1. Alerts Section --*/}
            {!alertsLoading && alerts && alerts.length > 0 && (
              <div className="space-y-3">
                {alerts.map((alert, index) => (
                  <Alert key={index} text={alert.text} type={alert.type} />
                ))}
              </div>
            )}

            {/*-- 2. Conditional DashboardHeader + RankCard --*/}
            <div className={`px-4 pb-0 sm:pb-4 space-y-4 ${!alertsLoading && alerts && alerts.length > 0 ? '' : 'pt-4'}`}>
              {isLoading ? (
                <DashboardHeaderSkeleton />
              ) : (
                <>
                  <DashboardHeader
                    toLearnToday={sentencesStats?.toLearnToday || 0}
                    toReviewNow={(sentencesStats?.reviewableNow || 0) + (wordsStats?.reviewableNow || 0)}
                  />
                  <RankCard
                    streak={currentStreak}
                    totalLearned={totalLearnedItems}
                    totalReviewed={totalReviewedItems}
                    listeningSeconds={listeningSeconds}
                  />
                </>
              )}
            </div>

            {/*-- 3. Conditional ActionCards --*/}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {isLoading ? (
                <>
                  <ActionCardSkeleton />
                  <ActionCardSkeleton />
                  <ActionCardSkeleton />
                </>
              ) : (
                <>
                  <div className="learning-button">
                    <ActionCard
                      type="learn"
                      title="Lessons"
                      description="Learn new sentences"
                      link="/session/learn"
                      count={sentencesStats?.toLearnToday || 0}
                      disabledText={sentencesStats?.showWkCapHint ? 'Limited by Kanji Complexity cap' : 'Done for today!'}
                      disabledTooltip={
                        sentencesStats?.showWkCapHint
                          ? 'Adjust your Kanji Complexity range in Settings.'
                          : null
                      }
                      suppressExtraStudy={!!sentencesStats?.showWkCapHint}
                      vacationStartedAt={vacationStartedAt}
                      emailVerified={emailVerified}
                      learnedToday={learnedToday}
                      showUpgradePrompt={sentencesStats?.showLearnUpgradePrompt}
                    />
                  </div>
                  <div className="review-sentences-button">
                    <ActionCard
                      type="review_sentences"
                      title="Sentence Reviews"
                      description="Review learned sentences"
                      link="/session/review/sentences"
                      count={sentencesStats?.reviewableNow || 0}
                      disabledText="No Reviews"
                      vacationStartedAt={vacationStartedAt}
                      emailVerified={emailVerified}
                    />
                  </div>
                  <div className="review-words-button">
                    <ActionCard
                      type="review_words"
                      title="Word Reviews"
                      description="Review learned words"
                      link="/session/review/words"
                      count={wordsStats?.reviewableNow || 0}
                      disabledText="No Reviews"
                      vacationStartedAt={vacationStartedAt}
                      emailVerified={emailVerified}
                    />
                  </div>
                </>
              )}
            </div>

            {/*-- 4. Conditional DashboardContentTabs --*/}
            {isLoading ? (
              <DashboardContentTabsSkeleton />
            ) : (
              <DashboardContentTabs
                stats={stats}
                currentStreak={currentStreak}
                protectedDaysUsed={protectedDaysUsed}
                shields={shields}
                protectedDates={protectedDates}
                vacationDates={vacationDates}
                userTimezone={userTimezone}
                isUserHourlySettingEnabled={isUserHourlySettingEnabled}
                upcomingReviewsSentences={upcomingReviewsSentences}
                upcomingReviewsWords={upcomingReviewsWords}
                learnedTodayData={learnedTodayData}
                reviewedSentencesData={reviewedSentencesData}
                reviewedWordsData={reviewedWordsData}
                playedSentencesData={playedSentencesData}
                playedWordsData={playedWordsData}
                listening={listening}
                cramWordsCount={cramWordsCount}
                cramSentencesCount={cramSentencesCount}
                recentUnlocksWordsCount={recentUnlocksWordsCount}
                recentUnlocksSentencesCount={recentUnlocksSentencesCount}
                learnedWordIds={learnedWordIds}
                learnedSentenceIds={learnedSentenceIds}
              />
            )}
          </div>
        </main>
      </div>
    </SkeletonTheme>
  );
}
export default DashboardPage;
